import {
  DEFAULT_MONITORING_CONFIG,
  MonitoringToolsConfig,
} from '../monitoring-tools.config';
import { AnomalyChecker } from './anomaly-checker.interface';
import { ClarityAnomalyChecker } from './clarity-anomaly-checker';
import { GTMAnomalyChecker } from './gtm-anomaly-checker';
import { PosthogAnomalyChecker } from './posthog-anomaly-checker';
import { SmartlookAnomalyChecker } from './smartlook-anomaly-checker';

/**
 * Factory for creating anomaly checkers based on configuration
 * Implements the Factory Pattern to create and manage monitoring tool validators
 */
export class AnomalyCheckerFactory {
  private static checkerRegistry = new Map<string, () => AnomalyChecker>([
    ['gtm', () => new GTMAnomalyChecker()],
    ['smartlook', () => new SmartlookAnomalyChecker()],
    ['posthog', () => new PosthogAnomalyChecker()],
    ['clarity', () => new ClarityAnomalyChecker()],
  ]);

  /**
   * Creates anomaly checkers based on the provided configuration
   * @param config Configuration specifying which monitoring tools to enable
   * @returns Array of enabled anomaly checkers
   */
  static createCheckers(
    config: MonitoringToolsConfig = DEFAULT_MONITORING_CONFIG,
  ): AnomalyChecker[] {
    const checkers: AnomalyChecker[] = [];
    const mergedConfig = { ...DEFAULT_MONITORING_CONFIG, ...config };

    for (const [key, createChecker] of this.checkerRegistry.entries()) {
      if (mergedConfig[key as keyof MonitoringToolsConfig] === true) {
        checkers.push(createChecker());
      }
    }

    return checkers;
  }

  /**
   * Registers a custom anomaly checker
   * Allows adding new monitoring tools dynamically
   * @param key Unique identifier for the checker
   * @param factory Factory function that creates the checker instance
   */
  static registerChecker(key: string, factory: () => AnomalyChecker): void {
    this.checkerRegistry.set(key, factory);
  }

  /**
   * Unregisters an anomaly checker
   * @param key Unique identifier of the checker to remove
   */
  static unregisterChecker(key: string): void {
    this.checkerRegistry.delete(key);
  }

  /**
   * Gets all registered checker keys
   * @returns Array of registered checker keys
   */
  static getRegisteredKeys(): string[] {
    return Array.from(this.checkerRegistry.keys());
  }
}
